import React from "react";
import { Users, Link, Palette, CalendarPlus, Sparkles } from "lucide-react";

// Mock UserData interface for demo
interface UserData {
  followers?: number;
  following?: any[];
  imagesGeneratedCount?: number;
  videoGeneratedCount?: number;
  eventsCreatedCount?: number;
  effectsCreatedCount?: number;
}

interface StatsOverviewProps {
  user: UserData;
  onFollowersClick: () => void;
  onFollowingClick: () => void;
}

const StatsOverview: React.FC<StatsOverviewProps> = ({ user, onFollowersClick, onFollowingClick }) => {
  const stats = [
    {
      name: "Followers",
      value: user.followers || 0,
      icon: Users,
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-500/10",
      iconColor: "text-blue-400",
      hoverColor: "hover:bg-blue-500/20",
      onClick: onFollowersClick,
    },
    {
      name: "Following",
      value: user.following?.length || 0,
      icon: Link,
      color: "from-purple-500 to-pink-500",
      bgColor: "bg-purple-500/10",
      iconColor: "text-purple-400",
      hoverColor: "hover:bg-purple-500/20",
      onClick: onFollowingClick,
    },
    {
      name: "Creations",
      value:
        (user.imagesGeneratedCount ?? 0) + (user.videoGeneratedCount ?? 0) || 0,
      icon: Palette,
      color: "from-orange-500 to-red-500",
      bgColor: "bg-orange-500/10",
      iconColor: "text-orange-400",
      hoverColor: "hover:bg-orange-500/20",
    },
    {
      name: "Events Created",
      value: user.eventsCreatedCount || 0,
      icon: CalendarPlus,
      color: "from-green-500 to-emerald-500",
      bgColor: "bg-green-500/10",
      iconColor: "text-green-400",
      hoverColor: "hover:bg-green-500/20",
    },
    {
      name: "Effects Created",
      value: user.effectsCreatedCount || 0,
      icon: Sparkles,
      color: "from-yellow-500 to-amber-500",
      bgColor: "bg-yellow-500/10",
      iconColor: "text-yellow-400",
      hoverColor: "hover:bg-yellow-500/20",
    },
  ];

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + "M";
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-2 rounded-2xl border border-gray-700/50 shadow-2xl">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        {stats.map((stat, index) => (
          <div
            key={stat.name}
            onClick={(stat as any).onClick}
            className={`relative group ${stat.bgColor} ${stat.hoverColor} backdrop-blur-sm border border-gray-700/30 rounded-xl p-3 sm:p-6 transition-all duration-200 ease-out hover:shadow-lg hover:border-gray-600/50 cursor-pointer will-change-transform`}
          >
            {/* Gradient overlay on hover */}
            <div
              className={`absolute inset-0 rounded-xl bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-200`}
            />

            {/* Content */}
            <div className="relative z-10 flex flex-col items-center text-center space-y-2 sm:space-y-3">
              {/* Icon */}
              <div
                className={`p-2 sm:p-3 rounded-full ${stat.bgColor} transition-transform duration-200 group-hover:scale-105`}
              >
                <stat.icon
                  className={`w-5 h-5 sm:w-8 sm:h-8 ${stat.iconColor}`}
                />
              </div>

              {/* Value and Label */}
              <div className="space-y-0.5 sm:space-y-1">
                <p className="text-xl sm:text-3xl font-bold text-white group-hover:text-gray-100 transition-colors duration-200">
                  {formatNumber(stat.value)}
                </p>
                <p className="text-xs sm:text-sm font-medium text-gray-400 group-hover:text-gray-300 transition-colors duration-200 uppercase tracking-wider">
                  {stat.name}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatsOverview;
